import React from 'react';
import {
  Search, LayoutDashboard, FolderKanban, FileText, MessageSquareCode,
  Rocket, Users, Settings, ShoppingBag, Dumbbell, Wallet, Bell,
  Sun, Coins, Hammer, ChevronRight
} from 'lucide-react';
import { recentProjectsMock } from '../mock';

const SideItem = ({ icon: Icon, label, active, badge }) => (
  <div
    className={`flex items-center gap-2.5 px-2.5 py-2 rounded-md text-[12px] ${
      active
        ? 'bg-gradient-to-r from-[#7c3aed]/30 to-transparent text-white border-l-2 border-[#a855f7]'
        : 'text-gray-400 hover:text-white'
    }`}
  >
    <Icon className="w-3.5 h-3.5" />
    <span>{label}</span>
    {badge && (
      <span className="ml-auto text-[9px] px-1.5 py-0.5 rounded-full bg-[#a855f7]/20 text-[#c4b5fd]">
        {badge}
      </span>
    )}
  </div>
);

const StatCard = ({ icon: Icon, label, value, trend, iconColor }) => (
  <div className="rounded-lg bg-[#0d0d15]/80 border border-white/5 p-3">
    <div className="flex items-center justify-between mb-1.5">
      <span className="text-[10px] text-gray-400">{label}</span>
      <Icon className={`w-3.5 h-3.5 ${iconColor}`} />
    </div>
    <div className="text-white text-lg font-bold">{value}</div>
    <div className="text-[9px] text-emerald-400 mt-0.5">{trend}</div>
  </div>
);

const iconMap = { ShoppingBag, Dumbbell, Wallet };

const ApptonomyMockup = () => {
  return (
    <div className="relative">
      {/* Glow */}
      <div className="absolute -inset-4 bg-gradient-to-br from-[#7c3aed]/20 via-transparent to-[#4f46e5]/10 blur-2xl" />

      <div className="relative rounded-2xl bg-[#0a0a12]/90 backdrop-blur-xl border border-white/10 shadow-[0_30px_80px_-20px_rgba(124,58,237,0.4)] overflow-hidden">
        {/* Top bar */}
        <div className="flex items-center gap-2 px-4 py-2.5 border-b border-white/5 bg-black/40">
          <div className="flex gap-1.5">
            <div className="w-2.5 h-2.5 rounded-full bg-red-500/70" />
            <div className="w-2.5 h-2.5 rounded-full bg-yellow-500/70" />
            <div className="w-2.5 h-2.5 rounded-full bg-green-500/70" />
          </div>
          <div className="flex items-center gap-1.5 ml-3 text-[11px] text-white font-semibold">
            <div className="w-4 h-4 rounded bg-gradient-to-br from-[#a855f7] to-[#4f46e5] flex items-center justify-center text-[8px] font-black">A</div>
            Apptonomy
          </div>
          <div className="flex-1 mx-4">
            <div className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-md px-2.5 py-1">
              <Search className="w-3 h-3 text-gray-500" />
              <span className="text-[10px] text-gray-500">Search anything...</span>
            </div>
          </div>
          <Bell className="w-3.5 h-3.5 text-gray-500" />
          <Sun className="w-3.5 h-3.5 text-gray-500" />
        </div>

        <div className="flex">
          {/* Sidebar */}
          <div className="w-[150px] border-r border-white/5 bg-black/20 p-2 space-y-0.5">
            <SideItem icon={LayoutDashboard} label="Dashboard" active />
            <SideItem icon={FolderKanban} label="Projects" />
            <SideItem icon={FileText} label="Templates" />
            <SideItem icon={MessageSquareCode} label="AI Chat" />
            <SideItem icon={Rocket} label="Deployments" badge="SOON" />
            <SideItem icon={Users} label="Team" />
            <SideItem icon={Settings} label="Settings" />

            <div className="pt-4 mt-3 border-t border-white/5 flex items-center gap-2 px-1">
              <div className="w-6 h-6 rounded-full bg-gradient-to-br from-[#a855f7] to-[#4f46e5] flex items-center justify-center text-[9px] font-bold text-white">J</div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] text-white font-medium truncate">John Founder</div>
                <div className="text-[8px] text-gray-500">Pro Plan</div>
              </div>
              <ChevronRight className="w-3 h-3 text-gray-500" />
            </div>
          </div>

          {/* Main */}
          <div className="flex-1 p-4">
            <div className="mb-4">
              <div className="text-white text-[15px] font-semibold">
                Good morning, John <span className="inline-block">👋</span>
              </div>
              <div className="text-[10px] text-gray-500">Let’s build something amazing today.</div>
            </div>

            <div className="grid grid-cols-3 gap-2.5 mb-4">
              <StatCard icon={FolderKanban} label="Projects" value="24" trend="↑ 12% this month" iconColor="text-purple-400" />
              <StatCard icon={Coins} label="Credits" value="8,560" trend="Available credits" iconColor="text-blue-400" />
              <StatCard icon={Hammer} label="Builds" value="142" trend="↑ 8% this week" iconColor="text-emerald-400" />
            </div>

            <div className="grid grid-cols-2 gap-3">
              {/* Recent Projects */}
              <div className="rounded-lg bg-[#0d0d15]/80 border border-white/5 p-2.5">
                <div className="text-[10px] text-gray-400 mb-2">Recent Projects</div>
                <div className="space-y-1.5">
                  {recentProjectsMock.map((p) => {
                    const Icon = iconMap[p.icon] || ShoppingBag;
                    return (
                      <div key={p.name} className="flex items-center gap-2">
                        <div className={`w-6 h-6 rounded-md bg-gradient-to-br ${p.color} flex items-center justify-center`}>
                          <Icon className="w-3 h-3 text-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-[9.5px] text-white truncate font-medium">{p.name}</div>
                          <div className="text-[8px] text-gray-500">{p.time}</div>
                        </div>
                        <span className="text-[8.5px] text-[#a855f7]">Preview ›</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Build Activity */}
              <div className="rounded-lg bg-[#0d0d15]/80 border border-white/5 p-2.5">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[10px] text-gray-400">Build Activity</div>
                  <div className="text-[16px] font-bold text-white">96%</div>
                </div>
                <svg viewBox="0 0 100 30" className="w-full h-10">
                  <defs>
                    <linearGradient id="chartFill" x1="0" x2="0" y1="0" y2="1">
                      <stop offset="0%" stopColor="#a855f7" stopOpacity="0.5" />
                      <stop offset="100%" stopColor="#a855f7" stopOpacity="0" />
                    </linearGradient>
                  </defs>
                  <path d="M0,25 L15,20 L30,22 L45,15 L60,17 L75,8 L90,10 L100,3 L100,30 L0,30 Z" fill="url(#chartFill)" />
                  <path d="M0,25 L15,20 L30,22 L45,15 L60,17 L75,8 L90,10 L100,3" stroke="#a855f7" strokeWidth="1.2" fill="none" />
                </svg>
                <div className="text-[8px] text-gray-500 -mt-1">Build success rates</div>

                <div className="mt-2 pt-2 border-t border-white/5">
                  <div className="text-[9px] text-gray-400 mb-1">AI Agents Active</div>
                  <div className="flex -space-x-1">
                    {['#f97316','#a855f7','#3b82f6','#10b981','#ec4899','#eab308'].map((c) => (
                      <div key={c} className="w-4 h-4 rounded-full border border-black" style={{ background: c }} />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApptonomyMockup;
